import toast from 'react-hot-toast';
import {
  COACH_SELF_CHECK_IN_NO_STUDENTS_MSG,
  COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG,
  getCoachSelfCheckInRosterBlockReason,
  shouldDisableCoachSelfCheckInForRoster,
} from '@court-sync/shared';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { Loader2 } from 'lucide-react';

export interface CoachSelfCheckInActionsProps {
  rosterCount: number;
  markedCount: number;
  /** When false, roster counts are still loading — do not disable check-in yet. */
  rosterStatsLoaded?: boolean;
  checkedIn: boolean;
  checkingIn: boolean;
  selfCheckInAvailable: boolean;
  selfCheckInLocked: boolean;
  onCheckIn: () => void | Promise<void>;
  onUndo: () => void | Promise<void>;
}

export function CoachSelfCheckInActions({
  rosterCount,
  markedCount,
  rosterStatsLoaded = true,
  checkedIn,
  checkingIn,
  selfCheckInAvailable,
  selfCheckInLocked,
  onCheckIn,
  onUndo,
}: CoachSelfCheckInActionsProps) {
  const blockReason = getCoachSelfCheckInRosterBlockReason(rosterCount, markedCount);
  const checkInDisabledForRoster =
    rosterStatsLoaded && shouldDisableCoachSelfCheckInForRoster(rosterCount, markedCount);

  const attemptCheckIn = () => {
    if (blockReason === 'no_students') {
      toast.error(COACH_SELF_CHECK_IN_NO_STUDENTS_MSG);
      return;
    }
    if (blockReason === 'incomplete_attendance') {
      toast.error(COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG);
      return;
    }
    void onCheckIn();
  };

  if (selfCheckInLocked) {
    return <span className="text-xs text-muted-foreground">Set by academy staff</span>;
  }

  if (checkedIn && selfCheckInAvailable) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-green-700 dark:text-green-300">Checked in</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-muted-foreground"
          disabled={checkingIn}
          onClick={() => void onUndo()}
        >
          Undo
        </Button>
      </div>
    );
  }

  if (!selfCheckInAvailable) {
    return (
      <span className="text-xs text-muted-foreground">Check-in available on session day</span>
    );
  }

  const checkInButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={checkingIn || checkInDisabledForRoster}
      onClick={attemptCheckIn}
    >
      {checkingIn ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          Saving…
        </>
      ) : (
        'Check in'
      )}
    </Button>
  );

  if (checkInDisabledForRoster) {
    return (
      <Tooltip content={COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG}>
        <span className="inline-flex">{checkInButton}</span>
      </Tooltip>
    );
  }

  return checkInButton;
}
